// ITEM 1 — CLOSING THE INBOX RETURNS HER TO THE SCREEN SHE OPENED IT FROM.
//
// HER FINDING, VERBATIM: *"The inbox popup has no way to close, you can only escape by clicking
// another button."* There WAS a control in that corner — S-1's back chevron — and her sentence is
// the honest reading of it: **it was a chevron labelled "Back" wired to `goTo("living-room")`**, a
// hard-typed destination that has nothing to do with where she came from. Open the inbox from My
// Room, tap the only thing that looks like a way out, and land in the Living Room. That is not a
// close, it is *another button*, which is exactly what she said.
//
// **SO THE CONTROL IS ONE CONTROL, NOT TWO.** Adding an X beside a chevron that still lies would
// leave two controls in one corner doing near-identical things, on a screen whose whole remaining
// order (items 3, 5 and 6) is about removing clutter. The chevron BECOMES the X and its destination
// becomes the true return.
//
// **THE DECISION HAS TWO OUTCOMES AND THEY ARE DIFFERENT FACTS (R18).** *We put the inbox on the
// history stack ourselves* and *she arrived here some other way* are not the same, and collapsing
// them is the one failure that leaves the site: `history.back()` on a DEEP LINK — a push
// notification's URL, a pasted link, a cold load on /inbox — walks out of 12th House entirely, into
// whatever she was looking at before her browser ever reached us. So the return is taken ONLY when
// this app is the thing that pushed the entry, proved by a stamp it wrote itself, and every other
// arrival gets a real page.
//
// **WHY A STAMP RATHER THAN `document.referrer` OR A LENGTH TEST.** `history.length` counts entries
// from other origins too, so it answers "there is something behind you" for a member who arrived
// from a search engine. `document.referrer` is empty on a same-document pushState. The only thing
// that can honestly say *we pushed this* is a value we wrote when we pushed it.
//
// **THE FALLBACK IS DECLARED WITH ITS REASON RATHER THAN CHOSEN.** `living-room` is where the retired
// chevron already went, so an arrival this law cannot place lands exactly where it landed before —
// the unknown case gets today's behaviour rather than a new one, and the inbox is a Living Room
// surface, so it is also the honest parent of the screen she is closing.
import type { Page } from "./pages";
// >>> ONE ANSWER TO *WHERE DOES A CLOSE GO* (R28/R29). ITEM 2's membership BACK button asks the same
// question one surface over, and two copies of a `history.back()` decision is two chances for one of
// them to walk a member out of 12th House entirely. The BODY moved; every name here is unchanged.
import { fromState, returnPath, runReturn, type ReturnPath } from "./return-path";

/** The key the stamp rides under. Written once, read once — never typed at a call site. */
export const INBOX_FROM_KEY = "inboxFrom";

/** Where an arrival this law cannot place is sent. See the header: it is the retired chevron's own destination. */
export const INBOX_RETURN_FALLBACK: Page = "living-room";

export type InboxReturn = ReturnPath;
type RetiredInboxReturn =
  /** This app pushed the inbox onto the stack, so the entry behind it is hers — go back to it, scroll and all. */
  | { kind: "back"; from: Page }
  /** She arrived some other way. There is no entry of ours behind this one, so a real page is named. */
  | { kind: "page"; page: Page };

/**
 * The state object to push when navigating TO the inbox.
 *
 * `page` is where she is standing at that moment. It is recorded rather than merely counted because
 * the verdict names it back — a report that says only "go back" cannot be checked against anything,
 * and a session reading the runbook wants to know WHICH screen was remembered.
 */
export function inboxFromState(page: Page): Record<string, unknown> {
  return fromState(INBOX_FROM_KEY, page);
}

/**
 * What the X should do, given `history.state` and the roster of pages that really exist.
 *
 * `known` is a PARAMETER rather than an import of `PAGE_PATHS`, so a gate drives this over rosters
 * this repo does not have and the law is not merely agreeing with today's page list (R129/R135).
 * A stamp naming a page nobody rosters is NOT a return — it is a stale or forged state object, and
 * trusting it would call `history.back()` on an entry that may not be ours (R136/R137's coercion
 * lesson one value over: only a real, rostered string is a page).
 */
/**
 * The whole close, side effects handed in.
 *
 * `back` and `go` are PARAMETERS for R26/R46's reason: the close was pure side effects on `window`,
 * which made a live browser the only instrument that could reach it. Out here a gate RUNS the whole
 * control — not merely the verdict — and reads back which door it took, so *the close calls back()
 * only on a stamped arrival* stops being a claim about a line of App.tsx and becomes arithmetic.
 */
export function runInboxClose(
  state: unknown,
  known: readonly string[],
  { back, go }: { back: () => void; go: (page: Page) => void },
): InboxReturn {
  return runReturn(state, known, INBOX_FROM_KEY, INBOX_RETURN_FALLBACK, { back, go });
}

export function inboxReturn(state: unknown, known: readonly string[]): InboxReturn {
  return returnPath(state, known, INBOX_FROM_KEY, INBOX_RETURN_FALLBACK);
}
